import type {
    SectionJson,
    SectionsConflictResolution,
    SectionsExportJson,
    SectionsImportConfig,
    SectionsImportConflict,
    SectionsImportReport,
    SectionsImportValidation
} from '@/api/types/core/sectionsJson';

import { createConfig } from '@/api/field_config';
import { createTranslation, updateTranslation } from '@/api/translations';

import { useSectionsStore } from '@/stores/sectionsStore';
import { ref, type Ref } from 'vue';



// Seuils de similarité
const SIMILARITY_THRESHOLDS = {
  HIGH: 90,    // Très probable doublon
  MEDIUM: 75,  // Attention requise (abaissé pour détecter Technic/Technique)
  LOW: 70      // Mention seulement
} as const;

export const useSectionsImport = (): {
  // État
  importing: Ref<boolean>;
  analyzing: Ref<boolean>;
  importError: Ref<string | null>;
  step: Ref<number>;
  
  // Données d'import
  selectedFile: Ref<File | null>;
  parsedData: Ref<SectionsExportJson | null>;
  validation: Ref<SectionsImportValidation | null>;
  conflicts: Ref<SectionsImportConflict[]>;
  resolutions: Ref<SectionsConflictResolution>;
  importConfig: Ref<SectionsImportConfig>;
  importReport: Ref<SectionsImportReport | null>;
  
  // Méthodes
  parseFile: () => Promise<void>;
  analyzeConflicts: () => Promise<void>;
  executeImport: () => Promise<void>;
  reset: () => void;
  updateResolution: (conflictId: string, resolution: string, customValue?: string) => void;
  calculateSimilarity: (name1: string, name2: string) => number;
  validateImportData: (data: SectionsExportJson) => SectionsImportValidation;
} => {
  const sectionsStore = useSectionsStore();
  
  // État principal
  const importing = ref(false);
  const analyzing = ref(false);
  const importError = ref<string | null>(null);
  const step = ref(1);
  
  // Données d'import
  const selectedFile = ref<File | null>(null);
  const parsedData = ref<SectionsExportJson | null>(null);
  const validation = ref<SectionsImportValidation | null>(null);
  const conflicts = ref<SectionsImportConflict[]>([]);
  const resolutions = ref<SectionsConflictResolution>({});
  const importReport = ref<SectionsImportReport | null>(null);
  
  // Configuration d'import par défaut
  const importConfig = ref<SectionsImportConfig>({
    create_missing_parents: true,
    repair_hierarchy: true,
    merge_translations: false,
    update_field_configs: true,
    skip_duplicates: false,
    conflict_resolution_mode: 'interactive'
  });

  /**
   * Calcule la similarité entre deux noms de sections (distance de Levenshtein)
   */
  const calculateSimilarity = (name1: string, name2: string): number => {
    const s1 = name1.toLowerCase();
    const s2 = name2.toLowerCase();
    
    if (s1 === s2) return 100;
    
    const matrix: number[][] = [];
    const len1 = s1.length;
    const len2 = s2.length;
    
    // Initialiser la matrice
    for (let i = 0; i <= len1; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len2; j++) {
      matrix[0][j] = j;
    }
    
    // Calculer la distance
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,      // suppression
          matrix[i][j - 1] + 1,      // insertion
          matrix[i - 1][j - 1] + cost // substitution
        );
      }
    }
    
    const distance = matrix[len1][len2];
    const maxLength = Math.max(len1, len2);
    const similarity = ((maxLength - distance) / maxLength) * 100;
    
    // Debug pour les cas intéressants
    if (similarity > 70) {
      console.log(`🔍 Similarité calculée: "${name1}" vs "${name2}" = ${similarity.toFixed(1)}% (distance: ${distance}, maxLength: ${maxLength})`);
    }
    
    return similarity;
  };

  /**
   * Valide les données d'import
   */
  const validateImportData = (data: SectionsExportJson): SectionsImportValidation => {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Vérifier la structure de base
    if (!data.export_info) {
      errors.push('Informations d\'export manquantes');
    } else {
      if (data.export_info.export_type !== 'sections') {
        errors.push('Type d\'export incorrect (attendu: sections)');
      }
      if (!data.export_info.version) {
        warnings.push('Version d\'export non spécifiée');
      }
    }
    
    if (!data.sections || !Array.isArray(data.sections)) {
      errors.push('Liste des sections manquante ou invalide');
    } else {
      // Vérifier chaque section
      data.sections.forEach((section, index) => {
        if (!section.id || typeof section.id !== 'string') {
          errors.push(`Section ${index + 1}: ID manquant ou invalide`);
        }
        
        // Vérifier les traductions
        if (section.translations) {
          Object.entries(section.translations).forEach(([lang, trans]) => {
            if (!trans.titre) {
              warnings.push(`Section ${section.id}: Titre manquant pour la langue ${lang}`);
            }
          });
        }
        
        // Vérifier les conditions
        if (section.field_config) {
          if (!section.field_config.condition_id || !section.field_config.condition_value) {
            warnings.push(`Section ${section.id}: Configuration de condition incomplète`);
          }
        }
      });
      
      // Vérifier les doublons dans l'import
      const sectionIds = data.sections.map(s => s.id);
      const duplicates = sectionIds.filter((id, index) => sectionIds.indexOf(id) !== index);
      if (duplicates.length > 0) {
        errors.push(`Sections dupliquées dans l'import: ${[...new Set(duplicates)].join(', ')}`);
      }
    }
    
    const sectionsCount = data.sections?.length || 0;
    const translationsCount = data.sections?.reduce((count, section) => {
      return count + (section.translations ? Object.keys(section.translations).length : 0);
    }, 0) || 0;
    const fieldConfigsCount = data.sections?.filter(s => s.field_config).length || 0;
    
    return {
      is_valid: errors.length === 0,
      errors,
      warnings,
      sections_count: sectionsCount,
      translations_count: translationsCount,
      field_configs_count: fieldConfigsCount,
      potential_conflicts: 0 // Sera calculé lors de l'analyse
    };
  };

  /**
   * Parse le fichier JSON sélectionné
   */
  const parseFile = async (): Promise<void> => {
    if (!selectedFile.value) {
      importError.value = 'Aucun fichier sélectionné';
      return;
    }
    
    analyzing.value = true;
    importError.value = null;
    
    try {
      const fileContent = await selectedFile.value.text();
      const data = JSON.parse(fileContent) as SectionsExportJson;
      
      // Valider les données
      const validationResult = validateImportData(data);
      validation.value = validationResult;
      
      if (!validationResult.is_valid) {
        importError.value = `Fichier invalide: ${validationResult.errors.join(', ')}`;
        return;
      }
      
      parsedData.value = data;
      step.value = 2;
      
    } catch (error) {
      console.error('❌ Erreur lors du parsing du fichier:', error);
      importError.value = error instanceof Error ? error.message : 'Erreur de parsing du fichier JSON';
    } finally {
      analyzing.value = false;
    }
  };

  /**
   * Détecte les sections similaires et existantes
   */
  const detectSimilarSections = (importedSections: SectionJson[]): SectionsImportConflict[] => {
    const conflicts: SectionsImportConflict[] = [];
    const existingSections = sectionsStore.sections;
    
    console.log('🔍 Détection des sections similaires...');
    console.log('Sections existantes:', existingSections.map(s => s.name));
    console.log('Sections à importer:', importedSections.map(s => s.id));
    
    importedSections.forEach(importedSection => {
      // Vérifier d'abord si la section existe exactement
      const exactMatch = existingSections.find(s => s.name === importedSection.id);
      
      if (exactMatch) {
        console.log(`⚠️ Section existante détectée: ${importedSection.id}`);
        conflicts.push({
          id: `existing_${importedSection.id}`,
          type: 'existing_section',
          section_name: importedSection.id,
          existing_value: exactMatch.name,
          imported_value: importedSection.id,
          similarity_percentage: 100,
          suggested_action: 'skip',
          auto_resolvable: true,
          resolution: 'skip',
          description: `Section "${importedSection.id}" existe déjà - sera ignorée`
        });
        return; // Ne pas chercher de similarités si exacte
      }
      
      // Chercher des sections similaires
      existingSections.forEach(existingSection => {
        const similarity = calculateSimilarity(importedSection.id, existingSection.name);
        
        console.log(`Comparaison: "${importedSection.id}" vs "${existingSection.name}" = ${similarity.toFixed(1)}%`);
        
        if (similarity >= SIMILARITY_THRESHOLDS.MEDIUM && similarity < 100) {
          console.log(`🔍 Section similaire détectée: ${importedSection.id} ≈ ${existingSection.name} (${similarity.toFixed(1)}%)`);
          conflicts.push({
            id: `similar_${importedSection.id}_${existingSection.name}`,
            type: 'similar_section',
            section_name: importedSection.id,
            existing_value: existingSection.name,
            imported_value: importedSection.id,
            similarity_percentage: similarity,
            suggested_action: similarity >= SIMILARITY_THRESHOLDS.HIGH ? 'align_existing' : 'create_new',
            auto_resolvable: false,
            resolution: 'create_new',
            description: `Section similaire détectée: "${importedSection.id}" ressemble à "${existingSection.name}" (${similarity.toFixed(1)}%)`
          });
        }
      });
    });
    
    console.log(`📊 ${conflicts.length} conflits de sections détectés`);
    return conflicts;
  };

  /**
   * Détecte les conflits de traductions
   */
  const detectTranslationConflicts = (importedSections: SectionJson[]): SectionsImportConflict[] => {
    const conflicts: SectionsImportConflict[] = [];
    const existingSections = sectionsStore.sections;
    
    console.log('🔍 Détection des conflits de traductions...');
    
    importedSections.forEach(importedSection => {
      const existingSection = existingSections.find(s => s.name === importedSection.id);
      
      if (existingSection && existingSection.translations && importedSection.translations) {
        console.log(`📝 Vérification traductions pour section: ${importedSection.id}`);
        
        Object.entries(importedSection.translations).forEach(([lang, importedTrans]) => {
          const existingTrans = existingSection.translations?.find(t => t.language_code === lang);
          
          if (existingTrans) {
            // Conflit sur le titre
            if (existingTrans.titre !== importedTrans.titre) {
              console.log(`⚠️ Conflit titre ${lang}: "${existingTrans.titre}" vs "${importedTrans.titre}"`);
              conflicts.push({
                id: `translation_title_${importedSection.id}_${lang}`,
                type: 'translation_title',
                section_name: importedSection.id,
                language_code: lang,
                field_name: 'titre',
                existing_value: existingTrans.titre || '',
                imported_value: importedTrans.titre,
                suggested_action: 'use_imported',
                auto_resolvable: false,
                resolution: 'use_imported',
                description: `Conflit de titre en ${lang}: "${existingTrans.titre}" vs "${importedTrans.titre}"`
              });
            }
            
            // Conflit sur la description
            if (existingTrans.description !== importedTrans.description && 
                (existingTrans.description || importedTrans.description)) {
              console.log(`⚠️ Conflit description ${lang}: "${existingTrans.description}" vs "${importedTrans.description}"`);
              conflicts.push({
                id: `translation_description_${importedSection.id}_${lang}`,
                type: 'translation_description',
                section_name: importedSection.id,
                language_code: lang,
                field_name: 'description',
                existing_value: existingTrans.description || '',
                imported_value: importedTrans.description || '',
                suggested_action: 'use_imported',
                auto_resolvable: false,
                resolution: 'use_imported',
                description: `Conflit de description en ${lang}: "${existingTrans.description || '(vide)'}" vs "${importedTrans.description || '(vide)'}"`
              });
            }
          } else {
            // Nouvelle traduction à ajouter
            console.log(`✅ Nouvelle traduction ${lang} pour section: ${importedSection.id}`);
          }
        });
      }
    });
    
    console.log(`📊 ${conflicts.length} conflits de traductions détectés`);
    return conflicts;
  };

  /**
   * Valide les conditions et détecte les conflits
   */
  const validateConditions = (importedSections: SectionJson[]): SectionsImportConflict[] => {
    const conflicts: SectionsImportConflict[] = [];
    const existingSections = sectionsStore.sections;
    
    importedSections.forEach(importedSection => {
      if (importedSection.field_config) {
        const { condition_id, condition_value } = importedSection.field_config;
        
        // Vérifier si la section de condition existe
        const conditionSectionExists = [...existingSections, ...importedSections].some(s => 
          ('name' in s ? s.name : s.id) === condition_id || s.id === condition_id
        );
        
        if (!conditionSectionExists) {
          conflicts.push({
            id: `invalid_condition_${importedSection.id}`,
            type: 'invalid_condition',
            section_name: importedSection.id,
            existing_value: '',
            imported_value: condition_id,
            condition_target: condition_id,
            suggested_action: 'create_new',
            auto_resolvable: true,
            resolution: 'create_new',
            description: `Section de condition manquante: "${condition_id}"`
          });
        }
        
        // Vérifier les conflits de conditions avec sections existantes
        const existingSection = existingSections.find(s => s.name === importedSection.id);
        if (existingSection && existingSection.fieldConfigs && existingSection.fieldConfigs.length > 0) {
          const existingConfig = existingSection.fieldConfigs[0];
          const existingConditionSection = existingSections.find(s => s.id === existingConfig.condition_id);
          
          if (existingConditionSection && 
              (existingConditionSection.name !== condition_id || existingConfig.condition_value !== condition_value)) {
            conflicts.push({
              id: `condition_conflict_${importedSection.id}`,
              type: 'condition_conflict',
              section_name: importedSection.id,
              existing_value: `${existingConditionSection.name}=${existingConfig.condition_value}`,
              imported_value: `${condition_id}=${condition_value}`,
              condition_target: condition_id,
              suggested_action: 'skip',
              auto_resolvable: false,
              resolution: 'skip',
              description: `Conflit de condition: existante "${existingConditionSection.name}=${existingConfig.condition_value}" vs importée "${condition_id}=${condition_value}"`
            });
          }
        }
      }
    });
    
    return conflicts;
  };

  /**
   * Analyse tous les conflits potentiels
   */
  const analyzeConflicts = async (): Promise<void> => {
    if (!parsedData.value) {
      importError.value = 'Aucune donnée à analyser';
      return;
    }
    
    analyzing.value = true;
    importError.value = null;
    
    try {
      // S'assurer que les données des sections sont à jour
      await sectionsStore.fetchSectionsData();
      
      const importedSections = parsedData.value.sections;
      const allConflicts: SectionsImportConflict[] = [];
      
      // Détecter les sections similaires
      allConflicts.push(...detectSimilarSections(importedSections));
      
      // Détecter les conflits de traductions
      allConflicts.push(...detectTranslationConflicts(importedSections));
      
      // Valider les conditions
      allConflicts.push(...validateConditions(importedSections));
      
      conflicts.value = allConflicts;
      
      // Initialiser les résolutions par défaut
      const defaultResolutions: SectionsConflictResolution = {};
      allConflicts.forEach(conflict => {
        defaultResolutions[conflict.id] = {
          resolution: conflict.resolution as any,
          custom_value: undefined
        };
      });
      resolutions.value = defaultResolutions;
      
      // Mettre à jour la validation avec le nombre de conflits
      if (validation.value) {
        validation.value.potential_conflicts = allConflicts.length;
      }
      
      step.value = 3;
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'analyse des conflits:', error);
      importError.value = error instanceof Error ? error.message : 'Erreur lors de l\'analyse des conflits';
    } finally {
      analyzing.value = false;
    }
  };

  /**
   * Met à jour la résolution d'un conflit
   */
  const updateResolution = (conflictId: string, resolution: string, customValue?: string): void => {
    resolutions.value[conflictId] = {
      resolution: resolution as any,
      custom_value: customValue
    };
  };

  /**
   * Exécute l'import avec les résolutions choisies
   */
  const executeImport = async (): Promise<void> => {
    if (!parsedData.value) {
      importError.value = 'Aucune donnée à importer';
      return;
    }
    
    importing.value = true;
    importError.value = null;
    const startTime = Date.now();
    
    console.log('🚀 Début de l\'import des sections...');
    
    try {
      const importedSections = parsedData.value.sections;
      const report: SectionsImportReport = {
        success: true,
        sections_created: 0,
        sections_updated: 0,
        sections_skipped: 0,
        translations_created: 0,
        translations_updated: 0,
        translations_skipped: 0,
        field_configs_created: 0,
        field_configs_updated: 0,
        field_configs_deleted: 0,
        hierarchy_repairs: 0,
        orphaned_sections_fixed: 0,
        conflicts: conflicts.value,
        duration_ms: 0
      };
      
      // Traiter chaque section importée
      for (const importedSection of importedSections) {
        console.log(`📝 Traitement de la section: ${importedSection.id}`);
        
        // Vérifier si la section existe déjà
        const existingSection = sectionsStore.sections.find(s => s.name === importedSection.id);
        
        if (existingSection) {
          // Section existante - traiter les traductions selon les résolutions
          console.log(`✏️ Mise à jour de la section existante: ${importedSection.id}`);
          
          // Traiter les traductions
          if (importedSection.translations) {
            for (const [lang, importedTrans] of Object.entries(importedSection.translations)) {
              const existingTrans = existingSection.translations?.find(t => t.language_code === lang);
              
              if (existingTrans) {
                // Vérifier les résolutions de conflits
                const titleConflictId = `translation_title_${importedSection.id}_${lang}`;
                const descConflictId = `translation_description_${importedSection.id}_${lang}`;
                
                const titleResolution = resolutions.value[titleConflictId]?.resolution;
                const descResolution = resolutions.value[descConflictId]?.resolution;
                
                // Mettre à jour le titre si résolution = use_imported
                if (titleResolution === 'use_imported' && existingTrans.titre !== importedTrans.titre) {
                  console.log(`🔄 Mise à jour titre ${lang}: "${existingTrans.titre}" → "${importedTrans.titre}"`);
                  await updateTranslation(existingTrans.id, { 
                    titre: importedTrans.titre,
                    language_code: existingTrans.language_code,
                    description: existingTrans.description || ''
                  });
                  report.translations_updated++;
                }
                
                // Mettre à jour la description si résolution = use_imported
                if (descResolution === 'use_imported' && existingTrans.description !== importedTrans.description) {
                  console.log(`🔄 Mise à jour description ${lang}: "${existingTrans.description}" → "${importedTrans.description}"`);
                  await updateTranslation(existingTrans.id, { 
                    titre: existingTrans.titre,
                    language_code: existingTrans.language_code,
                    description: importedTrans.description || '' 
                  });
                  report.translations_updated++;
                }
              } else {
                // Nouvelle traduction
                console.log(`➕ Ajout nouvelle traduction ${lang} pour: ${importedSection.id}`);
                await createTranslation({
                  reference_id: existingSection.id,
                  language_code: lang,
                  titre: importedTrans.titre,
                  description: importedTrans.description || ''
                });
                report.translations_created++;
              }
            }
          }
          
          report.sections_updated++;
        } else {
          // Nouvelle section - vérifier les résolutions de conflits similaires
          const similarConflicts = conflicts.value.filter(c => 
            c.type === 'similar_section' && c.section_name === importedSection.id
          );
          
          let shouldCreate = true;
          
          for (const conflict of similarConflicts) {
            const resolution = resolutions.value[conflict.id]?.resolution;
            if (resolution === 'skip' || resolution === 'align_existing') {
              shouldCreate = false;
              report.sections_skipped++;
              console.log(`⏭️ Section ignorée: ${importedSection.id} (résolution: ${resolution})`);
              break;
            }
          }
          
          if (shouldCreate) {
            console.log(`➕ Création nouvelle section: ${importedSection.id}`);
            
            // Créer la section
            const createResult = await sectionsStore.createSection({
              name: importedSection.id,
              type: importedSection.type || '',
              display: importedSection.display ?? true,
              mandatory_entry: importedSection.mandatory_entry ?? false,
              ordre: importedSection.ordre ?? 1,
              importance: importedSection.importance ?? 1
            });
            
            if (createResult.success) {
              // Récupérer la section créée pour avoir son ID
              await sectionsStore.fetchSectionsData();
              const newSection = sectionsStore.sections.find(s => s.name === importedSection.id);
              
              if (newSection) {
                report.sections_created++;
                
                // Ajouter les traductions
                if (importedSection.translations) {
                  for (const [lang, trans] of Object.entries(importedSection.translations)) {
                    console.log(`➕ Ajout traduction ${lang} pour nouvelle section: ${importedSection.id}`);
                    await createTranslation({
                      reference_id: newSection.id,
                      language_code: lang,
                      titre: trans.titre,
                      description: trans.description || ''
                    });
                    report.translations_created++;
                  }
                }
                
                // Ajouter les field_config si présents
                if (importedSection.field_config) {
                  console.log(`➕ Ajout field_config pour: ${importedSection.id}`);
                  
                  // Trouver l'ID de la section de condition
                  const conditionSection = sectionsStore.sections.find(s => s.name === importedSection.field_config!.condition_id);
                  if (conditionSection) {
                    await createConfig({
                      reference_id: newSection.id,
                      condition_id: conditionSection.id,
                      condition_value: importedSection.field_config.condition_value
                    });
                    report.field_configs_created++;
                  } else {
                    console.warn(`⚠️ Section de condition non trouvée: ${importedSection.field_config.condition_id}`);
                  }
                }
              }
            }
          }
        }
      }
      
      report.duration_ms = Date.now() - startTime;
      importReport.value = report;
      step.value = 4;
      
      console.log('✅ Import terminé avec succès:', report);
      
      // Rafraîchir les données des sections et traductions
      await sectionsStore.fetchSectionsData();
      await sectionsStore.fetchTranslations();
      
      console.log('🔄 Store rafraîchi - sections et traductions mises à jour');
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'import:', error);
      importError.value = error instanceof Error ? error.message : 'Erreur lors de l\'import';
      
      if (importReport.value) {
        importReport.value.success = false;
      }
    } finally {
      importing.value = false;
    }
  };

  /**
   * Remet à zéro l'état d'import
   */
  const reset = (): void => {
    step.value = 1;
    selectedFile.value = null;
    parsedData.value = null;
    validation.value = null;
    conflicts.value = [];
    resolutions.value = {};
    importReport.value = null;
    importError.value = null;
    importing.value = false;
    analyzing.value = false;
  };

  return {
    // État
    importing,
    analyzing,
    importError,
    step,
    
    // Données d'import
    selectedFile,
    parsedData,
    validation,
    conflicts,
    resolutions,
    importConfig,
    importReport,
    
    // Méthodes
    parseFile,
    analyzeConflicts,
    executeImport,
    reset,
    updateResolution,
    calculateSimilarity,
    validateImportData
  };
}; 